import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

/**
 * Seletor dos elementos que "consomem" o pointerdown: arrastar a partir deles
 * não deve panoramizar o container (senão o usuário não consegue clicar num
 * botão nem selecionar texto de uma célula).
 */
const INTERACTIVE = 'button, a, input, select, textarea, [role="button"], [data-no-pan]'

/** Deslocamento mínimo para considerar que houve arrasto, e não um clique trêmulo. */
const PAN_THRESHOLD_PX = 4

export interface PannableScrollViewport {
    /** Posição da janela visível, em % da largura total rolável. */
    left: number
    /** Largura da janela visível, em % da largura total. 100 = nada a rolar. */
    width: number
}

/**
 * Arrastar-para-rolar (drag-to-pan) num container com overflow, no mesmo padrão
 * do quadro de funil (`pipeline-detail-page.tsx`).
 *
 * Usa Pointer Events + `setPointerCapture` para que o arrasto continue mesmo se o
 * cursor sair do container — sem isso o pan "gruda" quando o mouse passa por cima
 * de outro elemento no meio do movimento.
 *
 * Expõe também o viewport para desenhar um minimapa (`<ScrollMinimap>`).
 */
export function usePannableScroll<T extends HTMLElement = HTMLDivElement>(
    /** Recalcula o viewport quando este valor muda (ex: linhas/colunas da tabela). */
    contentKey?: unknown
) {
    const ref = useRef<T | null>(null)
    const isPanning = useRef(false)
    const panStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 })
    /** Passou do limiar de arrasto? Impede que o fim de um pan vire clique. */
    const panMoved = useRef(false)
    const [viewport, setViewport] = useState<PannableScrollViewport>({
        left: 0,
        width: 100,
    })

    const updateViewport = useCallback(() => {
        const el = ref.current
        if (!el) return
        const scrollWidth = el.scrollWidth || 1
        const clientWidth = el.clientWidth || 1
        setViewport({
            left: (el.scrollLeft / scrollWidth) * 100,
            width: Math.min((clientWidth / scrollWidth) * 100, 100),
        })
    }, [])

    useEffect(() => {
        const el = ref.current
        if (!el) return
        el.addEventListener('scroll', updateViewport)
        // O conteúdo pode mudar de largura sem scroll (resize da janela, colunas novas).
        const observer = new ResizeObserver(updateViewport)
        observer.observe(el)
        updateViewport()
        return () => {
            el.removeEventListener('scroll', updateViewport)
            observer.disconnect()
        }
    }, [updateViewport, contentKey])

    /**
     * "Armado": houve um pointerdown válido, mas o arrasto ainda não começou.
     *
     * ⚠️ Não capturar o ponteiro nem chamar `preventDefault()` aqui. Ambos matam o
     * `click` que viria depois: `setPointerCapture` faz o browser redirecionar o
     * `click` para o elemento que capturou (o container), e `preventDefault()` no
     * `pointerdown` suprime os eventos de mouse de compatibilidade. O resultado é uma
     * tabela onde clicar numa linha não faz nada — e o comportamento ainda difere
     * entre Chrome e Safari. A captura só começa quando o ponteiro realmente anda.
     */
    const armed = useRef(false)
    const captured = useRef(false)

    const onPointerDown = useCallback((e: ReactPointerEvent) => {
        const el = ref.current
        if (!el) return
        panMoved.current = false
        armed.current = false
        captured.current = false

        if ((e.target as HTMLElement).closest(INTERACTIVE)) return
        // Nada a rolar: não sequestra o gesto (deixa clicar e selecionar texto).
        if (el.scrollWidth <= el.clientWidth) return

        armed.current = true
        panStart.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: el.scrollLeft,
            scrollTop: el.scrollTop,
        }
    }, [])

    const onPointerMove = useCallback((e: ReactPointerEvent) => {
        if (!armed.current || !ref.current) return
        const el = ref.current
        const dx = e.clientX - panStart.current.x
        const dy = e.clientY - panStart.current.y

        // Limiar de 4px: tremor de mão ao clicar não deve virar arrasto.
        if (!panMoved.current) {
            if (Math.abs(dx) <= PAN_THRESHOLD_PX && Math.abs(dy) <= PAN_THRESHOLD_PX) {
                return
            }
            panMoved.current = true
            isPanning.current = true
            // Só agora capturamos: o clique-sem-arrasto já passou ileso.
            el.setPointerCapture(e.pointerId)
            captured.current = true
            // Cursor via style (e não estado) para não re-renderizar a cada arrasto.
            el.style.cursor = 'grabbing'
            // O gesto começou como possível seleção de texto; desfaz.
            window.getSelection()?.removeAllRanges()
        }

        // Impede que o arrasto continue estendendo uma seleção de texto.
        e.preventDefault()
        el.scrollLeft = panStart.current.scrollLeft - dx
        el.scrollTop = panStart.current.scrollTop - dy
    }, [])

    const onPointerUp = useCallback((e: ReactPointerEvent) => {
        armed.current = false
        if (!isPanning.current) return
        isPanning.current = false
        if (ref.current) {
            ref.current.style.cursor = ''
            // Só libera se chegou a capturar (arrasto além do limiar).
            if (captured.current) ref.current.releasePointerCapture(e.pointerId)
        }
        captured.current = false
    }, [])

    /** `true` quando há conteúdo fora da área visível — controla exibir o minimapa. */
    const hasOverflow = viewport.width < 100

    /**
     * O último gesto foi um arrasto? Chame no `onClick` de elementos clicáveis
     * dentro do container: o `click` dispara depois do `pointerup`, então sem isso
     * todo pan termina abrindo o que estiver sob o cursor.
     */
    const didPan = useCallback(() => panMoved.current, [])

    return {
        ref,
        viewport,
        hasOverflow,
        didPan,
        panHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
    }
}
